import React, { useEffect, useState } from 'react';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPAPageHeader from '@/components/admin/SysadminSPA/shared/SPAPageHeader';
import cn from '@/utils/cn';
import type { ChatMessage, PromptDetail as PromptDetailType, TestResult } from './types';
import type { SavePromptPayload } from './useLangfusePrompts';
import PromptEditor from './PromptEditor';
import PromptTester from './PromptTester';
import PromptVersionSelector from './PromptVersionSelector';

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  isTesting: boolean;
  onSave: (payload: SavePromptPayload) => Promise<boolean>;
  onTest: (variables: Record<string, string>) => void;
  onVersionChange: (version: number) => void;
  prompt: PromptDetailType;
  testResult: TestResult | null;
}

const labelChipClass = (production: boolean) => cn(
  'tw-rounded tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-secondary',
  'tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium',
  production ? 'tw-text-spa-success' : 'tw-text-spa-secondary',
);

const PromptDetail = ({
  isLoading,
  isSaving,
  isTesting,
  onSave,
  onTest,
  onVersionChange,
  prompt,
  testResult,
}: Props) => {
  const [editedPrompt, setEditedPrompt] = useState<string | ChatMessage[]>(prompt.prompt);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setEditedPrompt(prompt.prompt);
    setIsEditing(false);
  }, [prompt.name, prompt.version]);

  const handleSave = async () => {
    const saved = await onSave({
      config: prompt.config,
      labels: prompt.labels,
      name: prompt.name,
      prompt: editedPrompt,
      type: prompt.type,
    });
    if (saved) setIsEditing(false);
  };

  const cancelEdit = () => {
    setEditedPrompt(prompt.prompt);
    setIsEditing(false);
  };

  // Build version range for selector: show up to current version
  const versions = Array.from({ length: prompt.version }, (_, i) => prompt.version - i);

  return (
    <section aria-busy={isLoading}>
      <SPAPageHeader
        actions={(
          <div className="tw-flex tw-items-center tw-gap-2">
            {isEditing ? (
              <>
                <SPAButton onClick={cancelEdit}>Cancel</SPAButton>
                <SPAButton disabled={isSaving} variant="primary" onClick={handleSave}>
                  {isSaving ? 'Saving…' : 'Save as new version'}
                </SPAButton>
              </>
            ) : (
              <SPAButton variant="outline" onClick={() => setIsEditing(true)}>Edit</SPAButton>
            )}
          </div>
        )}
        title={prompt.name}
      />
      <div className="tw-mb-5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2">
        <PromptVersionSelector
          currentVersion={prompt.version}
          versions={versions}
          onVersionChange={onVersionChange}
        />
        {prompt.labels?.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
            {prompt.labels.map(label => (
              <span key={label} className={labelChipClass(label === 'production')}>{label}</span>
            ))}
          </div>
        )}
        {prompt.variables?.length > 0 && (
          <span className="tw-text-xs tw-text-spa-tertiary">
            Variables: {prompt.variables.map(v => `{{${v}}}`).join(', ')}
          </span>
        )}
      </div>

      <PromptEditor
        prompt={{ ...prompt, prompt: editedPrompt }}
        readOnly={!isEditing}
        onChange={setEditedPrompt}
      />

      <PromptTester
        key={prompt.name}
        isTesting={isTesting}
        testResult={testResult}
        variables={prompt.variables || []}
        onTest={onTest}
      />
    </section>
  );
};

export default PromptDetail;
